import { z } from "zod";
import { DemoNavigationStepSchema, DemoObservationSchema, type DemoNavigationStep } from "./demoTutorial.js";

export const DEMO_STEPS = ["dialogue", "shop", "reward"] as const;
export const DemoStepSchema = z.enum(DEMO_STEPS);
export type DemoStep = z.infer<typeof DemoStepSchema>;
export const DEMO_TOUR_VERSION = 3;

export const DemoTargetSchema = z.object({
	entityId: z.string().min(1),
	nodeId: z.string().min(1).optional(),
});
export const DemoTargetsSchema = z.object({
	dialogue: DemoTargetSchema.extend({ nodeId: z.string().min(1) }),
	shop: DemoTargetSchema,
	reward: DemoTargetSchema.extend({ nodeId: z.string().min(1) }),
});
export type DemoTargets = z.infer<typeof DemoTargetsSchema>;
export type DemoStepProgress = {
	learning?: Partial<Record<DemoNavigationStep, string>>;
	baseline: string;
	original?: Record<string, unknown>;
	edit: string | null;
	editId?: string | null;
	savedAt: string | null;
	completedAt: string | null;
	skippedAt: string | null;
};
export type DemoProgress = Record<DemoStep, DemoStepProgress>;
export const DemoClientEventSchema = z
	.object({
		event: z.enum([
			"guided_step_started",
			"guided_substep_started",
			"guided_substep_completed",
			"guided_value_changed",
			"demo_launch_requested",
			"demo_baseline_launch_requested",
			"demo_personalization_started",
			"demo_continue_exploring",
			"guided_tour_paused",
			"guided_tour_resumed",
			"guided_step_skipped",
			"guided_step_selected",
			"guided_tour_dismissed",
			"studio_setup_started",
			"demo_error",
		]),
		step: DemoStepSchema,
		substep: DemoNavigationStepSchema.optional(),
		observation: DemoObservationSchema.optional(),
		code: z.enum(["target_missing", "launch_failed", "save_failed"]).optional(),
	})
	.strict();
export type DemoClientEvent = z.infer<typeof DemoClientEventSchema>;
export type DemoJourney = {
	phase: "discover" | "personalize" | "tutorial";
	launchRequestedAt: string | null;
	sessionReadyAt: string | null;
	interactedAt: string | null;
};
export type HostedDemoState = {
	enabled: boolean;
	linked: boolean;
	placeId: string | null;
	demo: {
		projectId: string;
		slug: string;
		activeStep: DemoStep;
		paused: boolean;
		tourVersion: number;
		targets: DemoTargets;
		progress: DemoProgress;
		loadedRevision: string | null;
		loadedEdits?: Record<DemoStep, string | null>;
		journey: DemoJourney;
	} | null;
};
